/**
 * Typed client for the asic-router backend API.
 *
 * The frontend talks to the Cloudflare Worker at same-origin `/api/*`.
 * The Worker proxies to the Go backend on the VPS, so from the browser's
 * perspective there is never a cross-origin fetch. This file describes
 * the request/response shapes that both sides agree on.
 *
 * The backend reference implementation is `cmd/asicr/serve.go` in the
 * asic-router Go repo. If you change a field here, update the server
 * encoder there to match.
 */

import type { TokenSymbol } from "./tokens";

// ------------------------------------------------------------------
//                          Shared shapes
// ------------------------------------------------------------------

export type QuoteDirection = "USDC->ASIC" | "ASIC->USDC";

export interface PoolRef {
  id: string;
  label: string;
  address: string;
}

export interface SplitLeg {
  /** The router's internal path identifier, e.g. `rev-plsb-v3-1-v3-1`. */
  pathId: string;
  /** Raw wei-scale input going through this path. */
  amountIn: string;
  /** Human-readable decimal of amountIn. */
  amountInFormatted: string;
  /** Raw wei-scale output this path delivers. */
  amountOut: string;
  /** Human-readable decimal of amountOut. */
  amountOutFormatted: string;
  /** Fractional share of total input [0, 100]. */
  sharePct: number;
  /** Ordered list of pools the path traverses, first hop -> last. */
  pools: PoolRef[];
}

export interface QuoteResponse {
  blockNumber: number;
  direction: QuoteDirection;
  inputToken: TokenSymbol;
  outputToken: TokenSymbol;
  amountIn: string;
  amountInFormatted: string;
  totalOut: string;
  totalOutFormatted: string;
  /** Best single-path output, for uplift comparison. */
  naiveBestSolo: string;
  naiveBestPathId: string;
  /** Relative uplift (0-100) of the split vs the best single path. */
  splittingUpliftPct: number;
  /** Water-filling final lambda (decimal string, for debugging). */
  lambdaStar: string;
  /** Outer-loop bisection count, for debugging. */
  iterations: number;
  /** Server-side solve wall time in milliseconds. */
  elapsedMs: number;
  splits: SplitLeg[];
}

export interface BuildCalldataRequest {
  amount: string;
  direction: QuoteDirection;
  recipient: string;
  slippageBps: number;
  deadlineSecs: number;
}

export interface BuildCalldataResponse {
  /** Deployed AsicExecutor address on mainnet. */
  to: string;
  /** ABI-encoded calldata, 0x-prefixed. */
  data: string;
  /** Hex-encoded msg.value (always "0x0" in v1). */
  value: string;
  /** Raw wei minOutTotal sent into the executor. */
  minOutTotal: string;
  minOutTotalFormatted: string;
  deadline: number;
}

export interface ApiError {
  error: string;
}

// ------------------------------------------------------------------
//                             Client
// ------------------------------------------------------------------

/**
 * Fetch a live quote from the backend.
 *
 * `amount` is a human-readable decimal string (e.g. "100" for 100 USDC
 * or "5663.81" for 5663.81 ASIC). The backend parses it according to
 * the input token's decimals.
 */
export async function fetchQuote(
  amount: string,
  direction: QuoteDirection,
  opts: { signal?: AbortSignal } = {},
): Promise<QuoteResponse> {
  const params = new URLSearchParams({ amount, direction });
  const res = await fetch(`/api/quote?${params.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal: opts.signal,
  });
  if (!res.ok) {
    const body = await safeJson<ApiError>(res);
    throw new Error(body?.error ?? `quote failed (${res.status})`);
  }
  return res.json();
}

/**
 * Ask the backend to compose AsicExecutor calldata for an outstanding
 * quote, including the caller's recipient address and slippage floor.
 * The caller is still responsible for sending the tx — this only
 * returns bytes.
 */
export async function buildCalldata(
  req: BuildCalldataRequest,
  opts: { signal?: AbortSignal } = {},
): Promise<BuildCalldataResponse> {
  const res = await fetch(`/api/build-calldata`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(req),
    signal: opts.signal,
  });
  if (!res.ok) {
    const body = await safeJson<ApiError>(res);
    throw new Error(body?.error ?? `build-calldata failed (${res.status})`);
  }
  return res.json();
}

/**
 * Liveness probe for the footer status indicator.
 */
export async function checkHealth(
  opts: { signal?: AbortSignal } = {},
): Promise<boolean> {
  try {
    const res = await fetch(`/api/health`, { signal: opts.signal });
    return res.ok;
  } catch {
    return false;
  }
}

async function safeJson<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}
